import React, { useState, useEffect } from 'react';
import { Zap, X, ArrowUpRight } from 'lucide-react';

interface TrialBannerProps {
  /** Trial expiration date - defaults to 30/Jan/2026 00:00 GMT */
  expirationDate?: Date | string;
  /** Callback when upgrade is clicked */
  onUpgradeClick?: () => void;
  /** Custom upgrade URL */
  upgradeUrl?: string;
}

const SESSION_STORAGE_KEY = 'insight-trial-banner-dismissed';

export const TrialBanner: React.FC<TrialBannerProps> = ({
  expirationDate,
  onUpgradeClick,
  upgradeUrl = '/contact-sales',
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner was dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  };

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else if (upgradeUrl) {
      window.open(upgradeUrl, '_blank');
    }
  };

  // Format expiration date
  const formatExpirationDate = (): string => {
    if (!expirationDate) {
      // Default: 30/Jan/2026 00:00 GMT
      return '30/Jan/2026 00:00 GMT';
    }

    const date = typeof expirationDate === 'string' 
      ? new Date(expirationDate) 
      : expirationDate;

    if (isNaN(date.getTime())) {
      return '30/Jan/2026 00:00 GMT';
    }

    const day = date.getUTCDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes} GMT`;
  };

  // NOTE: If we later want to display “X days left”, add a days-remaining helper here.

  if (isDismissed) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between px-3 py-1 border-b min-h-[28px] max-h-[32px] bg-semantic-bg-warning-secondary border-semantic-border-warning-secondary">
        {/* Left side - Trial message */}
        <div className="flex items-center gap-1.5 flex-1">
          <Zap className="w-3 h-3 text-semantic-text-warning flex-shrink-0" />
          <span className="text-[11px] font-medium text-semantic-text-warning truncate">
            Free Trial - Until {formatExpirationDate()}
          </span>
        </div>

        {/* Right side - Upgrade link and dismiss */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleUpgradeClick}
            className="flex items-center gap-0.5 text-[11px] font-semibold text-semantic-text-warning hover:opacity-80 transition-opacity"
          >
            <span>Upgrade</span>
            <ArrowUpRight className="w-3 h-3" />
          </button>
          <button
            onClick={handleDismiss}
            className="p-0.5 text-semantic-text-warning opacity-70 hover:opacity-100 rounded transition-opacity"
            title="Dismiss for this session"
            aria-label="Dismiss trial banner"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;

